import InputText from 'app/components/flat/ui/Inputs/InputText';
import InputCardNumber from 'app/components/flat/ui/Inputs/InputCardNumber';
import PaymentMethodCard from 'app/components/flat/ui/PaymentMethodCard';
import { formatCardExpirationDisplay } from 'app/components/SubscriptionRegularizationModals/RegularizeCardPaymentModal/utils';

import type { PaymentMethodSelectorProps } from './types';
import * as S from './styles';

const SAVED_CARD_LABEL = 'Utilizar cartão já cadastrado';
const NEW_CARD_LABEL = 'Cadastrar novo cartão';
const PAYMENT_METHOD_RADIO_GROUP = 'checkout-payment-method';
const SAVED_CARD_RADIO_GROUP = 'checkout-saved-card';
// Explícito nos 4 campos da linha (em vez de depender do default do InputText/InputCardNumber),
// para não dessincronizar se um dos dois componentes mudar sua altura padrão isoladamente.
const FIELD_HEIGHT = '50px';

export function PaymentMethodSelector({
  mode,
  hasSavedCard,
  savedCards,
  selectedSavedCardId,
  onSelectSavedCard,
  onSelectNewCard,
  formik,
}: PaymentMethodSelectorProps) {
  if (mode === 'loading') {
    return null;
  }

  const isNewCard = mode === 'new';

  const newCardFields = (
    <S.NewCardFields>
      <div>
        <InputText
          label="Nome impresso no cartão"
          name="cardholder_name"
          value={formik.values.cardholder_name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Nome completo"
          inputHeight={FIELD_HEIGHT}
        />
        {formik.touched.cardholder_name && formik.errors.cardholder_name ? (
          <S.ErrorMessage>{formik.errors.cardholder_name}</S.ErrorMessage>
        ) : null}
      </div>

      <S.FieldRow>
        <div>
          <InputCardNumber
            label="Número do cartão"
            name="card_number"
            value={formik.values.card_number}
            onChange={(value) => formik.setFieldValue('card_number', value)}
            onBlur={formik.handleBlur}
            inputHeight={FIELD_HEIGHT}
          />
          {formik.touched.card_number && formik.errors.card_number ? (
            <S.ErrorMessage>{formik.errors.card_number}</S.ErrorMessage>
          ) : null}
        </div>

        <div>
          <InputText
            label="Vencimento"
            name="card_expiration"
            value={formatCardExpirationDisplay(formik.values.card_expiration)}
            onChange={(event) => {
              const value = event.target.value.replace(/\D/g, '');
              if (value.length <= 4) {
                formik.setFieldValue('card_expiration', value);
              }
            }}
            onBlur={formik.handleBlur}
            placeholder="MM/AA"
            maxLength={5}
            inputHeight={FIELD_HEIGHT}
          />
          {formik.touched.card_expiration && formik.errors.card_expiration ? (
            <S.ErrorMessage>{formik.errors.card_expiration}</S.ErrorMessage>
          ) : null}
        </div>

        <div>
          <InputText
            label="CVV"
            name="card_cvv"
            value={formik.values.card_cvv}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="000"
            maxLength={4}
            inputHeight={FIELD_HEIGHT}
          />
          {formik.touched.card_cvv && formik.errors.card_cvv ? (
            <S.ErrorMessage>{formik.errors.card_cvv}</S.ErrorMessage>
          ) : null}
        </div>
      </S.FieldRow>
    </S.NewCardFields>
  );

  if (!hasSavedCard) {
    return (
      <S.Wrapper>
        <S.SectionLabel>Forma de pagamento</S.SectionLabel>
        <S.Container>{newCardFields}</S.Container>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.SectionLabel>Forma de pagamento</S.SectionLabel>
      <S.Container>
        <S.Option $expanded={!isNewCard}>
          <S.OptionLabel $expanded={!isNewCard}>
            <input
              type="radio"
              name={PAYMENT_METHOD_RADIO_GROUP}
              checked={!isNewCard}
              onChange={() => onSelectSavedCard()}
            />
            {SAVED_CARD_LABEL}
          </S.OptionLabel>
          {!isNewCard ? (
            <S.OptionBody>
              <S.SavedCardsList>
                {savedCards.map((card) => (
                  <S.SavedCardOption key={card.id}>
                    <input
                      type="radio"
                      name={SAVED_CARD_RADIO_GROUP}
                      checked={card.id === selectedSavedCardId}
                      onChange={() => onSelectSavedCard(card.id)}
                    />
                    <PaymentMethodCard
                      brandLabel={card.brandLabel}
                      brandIconUrl={card.brandIconUrl}
                      lastDigits={card.lastDigits}
                      expirationDate={card.expirationDate}
                      isPrimary={card.isPrimary}
                    />
                  </S.SavedCardOption>
                ))}
              </S.SavedCardsList>
            </S.OptionBody>
          ) : null}
        </S.Option>

        <S.Option $expanded={isNewCard}>
          <S.OptionLabel $expanded={isNewCard}>
            <input
              type="radio"
              name={PAYMENT_METHOD_RADIO_GROUP}
              checked={isNewCard}
              onChange={onSelectNewCard}
            />
            {NEW_CARD_LABEL}
          </S.OptionLabel>
          {isNewCard ? <S.OptionBody>{newCardFields}</S.OptionBody> : null}
        </S.Option>
      </S.Container>
    </S.Wrapper>
  );
}

export default PaymentMethodSelector;
